import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LinkedInCrawlerContact } from 'features/co-pilot/co-pilot.types';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  contactsToAdd: LinkedInCrawlerContact[];

  handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleDeleteContacts(): void;
}

const DataNotAvailable = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Typography className={classes.notAvailableText}>{t('dataNotAvailable')}</Typography>
    </Stack>
  );
};

const ManualContactList: React.FC<Props> = ({ contactsToAdd, handleFileChange, handleDeleteContacts }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const MAX_CONTACTS_PER_PAGE = 100;
  const [page, setPage] = useState(1);

  const paginatedContacts = contactsToAdd.slice(
    (page - 1) * MAX_CONTACTS_PER_PAGE,
    page * MAX_CONTACTS_PER_PAGE
  );

  const getVisibleColumns = () => {
    const fields = {
      name: false,
      occupation: false,
      currentCompanies: false,
      email: false,
      url: false,
      phoneNumber: false, // <-- added phoneNumber
    };

    contactsToAdd.forEach((contact) => {
      if (contact.name) fields.name = true;
      if (contact.occupation) fields.occupation = true;
      if (contact.currentCompanies?.length) fields.currentCompanies = true;
      if (contact.email) fields.email = true;
      if (contact.url) fields.url = true;
      if (contact.phoneNumber) fields.phoneNumber = true;
    });

    return fields;
  };

  const visibleColumns = getVisibleColumns();

  const handleChangePage = (event: any, value: number) => {
    setPage(value);
  };

  return (
    <div className={classes.container}>
      <div className={classes.containerHeader}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={3}>
          <PaiLink
            text={t('uploadMoreFiles')}
            size={'md'}
            underline={'always'}
            IconComponent={<CloudUploadIcon />}
            allowFileUpload={true}
            handleFileUpload={handleFileChange}
          />
          <PaiLink
            text={t('deleteContacts')}
            size={'md'}
            underline={'always'}
            IconComponent={<DeleteIcon />}
            action={handleDeleteContacts}
          />
        </Stack>
      </div>
      <div className={classes.containerBody}>
        <div className={classes.contactList}>
          <Stack
            className={classes.listHeader}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
          >
            <Typography className={classes.greyText}>
              {t('leadsCount', { count: contactsToAdd.length })}
            </Typography>
          </Stack>
          <div className={classes.listContent}>
            <TableContainer sx={{ height: '100%', paddingRight: '32px' }}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow className={classes.tableHeadRow}>
                    {visibleColumns.name && (
                      <TableCell sx={{ minWidth: '20%' }}>{t('name')}</TableCell>
                    )}
                    {visibleColumns.occupation && (
                      <TableCell sx={{ minWidth: '20%' }}>{t('position')}</TableCell>
                    )}
                    {visibleColumns.currentCompanies && (
                      <TableCell sx={{ minWidth: '20%' }}>{t('company')}</TableCell>
                    )}
                    {visibleColumns.email && (
                      <TableCell sx={{ minWidth: '20%' }}>{t('email')}</TableCell>
                    )}
                    {visibleColumns.phoneNumber && (
                      <TableCell sx={{ minWidth: '20%' }}>{t('phoneNumber')}</TableCell>
                    )}
                    {visibleColumns.url && (
                      <TableCell sx={{ minWidth: '20%' }}>{t('linkedinUrl')}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {/* This extra row for some spacing */}
                    <TableCell colSpan={6} sx={{ height: '24px', borderBottom: 'none' }} />
                  </TableRow>
                  {paginatedContacts.map((contact, index) => (
                    <TableRow key={index}>
                      {visibleColumns.name && <TableCell>{contact.name}</TableCell>}
                      {visibleColumns.occupation && (
                        <TableCell>{contact.occupation || <DataNotAvailable />}</TableCell>
                      )}
                      {visibleColumns.currentCompanies && (
                        <TableCell>
                          {_.truncate(contact.currentCompanies?.join(', '), { length: 30 }) || (
                            <DataNotAvailable />
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.email && (
                        <TableCell>
                          {_.truncate(contact.email, { length: 30 }) || <DataNotAvailable />}
                        </TableCell>
                      )}
                      {visibleColumns.phoneNumber && (
                        <TableCell>
                          {_.truncate(contact.phoneNumber, { length: 30 }) || (
                            <DataNotAvailable />
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.url && (
                        <TableCell>
                          {contact.url ? (
                            <PaiLink
                              text={t('open')}
                              size={'md'}
                              variant={'primary'}
                              IconComponent={<OpenInNewIcon />}
                              href={contact.url}
                              target={'_blank'}
                            />
                          ) : (
                            <DataNotAvailable />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          <Stack
            className={classes.listFooter}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
          >
            <PaiPagination
              total={contactsToAdd.length}
              itemsPerPage={MAX_CONTACTS_PER_PAGE}
              page={page}
              handleChangePage={handleChangePage}
            />
          </Stack>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '100%',
    borderRadius: '12px',
    border: `2px dashed ${theme.palette.greys.light}`,
  },
  containerHeader: {
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  containerBody: {
    padding: '24px',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  contactList: {
    width: '100%',
    borderRadius: '12px',
    border: `2px solid ${theme.palette.primary.main}`,
    background: '#FFF',
  },
  listHeader: {
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  listContent: {
    position: 'relative',
    height: '380px',
    padding: '24px 0 24px 32px',
  },
  tableHeadRow: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    position: 'sticky',
    top: 0,
    zIndex: 9,
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      color: '#FFF',
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& td': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
  },
  notAvailableText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.light,
  },
  listFooter: {
    padding: '8px 16px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
}));

export default ManualContactList;
